/**
 * GWP Engine — Gift With Purchase cart automation (Déesse PRO)
 * Port of the iS Clinical gwp-engine.js. Rules come from the GWP Rule metaobjects,
 * rendered as JSON by snippets/gwp-rules-json.liquid (no app proxy needed).
 * Evaluates the cart, auto-adds/removes gifts and renders progress messages.
 *
 * Gift lines carry properties { _gwp: 'true', _gwp_rule_id: <rule handle> } so the
 * FS cart drawer shows them as "Complimentary" with no quantity/remove controls.
 * The gift is made free at checkout by a matching automatic discount per rule.
 */
(function () {
  'use strict';

  var GWP_PROPERTY = '_gwp_rule_id';
  var COOLDOWN_MS = 200;
  var FETCH_TIMEOUT = 5000;
  var MAX_OPS_PER_MINUTE = 10;
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ROUTES = {
    cart: ROOT + 'cart.js',
    add: ROOT + 'cart/add.js',
    change: ROOT + 'cart/change.js'
  };

  var state = {
    rules: null,
    processing: false,
    opsThisMinute: 0,
    opsResetTimer: null,
    gwpFetch: false
  };

  // ─── Utility helpers ───

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str == null ? '' : String(str)));
    return div.innerHTML;
  }

  function fetchWithTimeout(url, options) {
    state.gwpFetch = true;
    var result = Promise.race([
      nativeFetch(url, options),
      new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('Timeout')); }, FETCH_TIMEOUT);
      })
    ]);
    return result.finally(function () { state.gwpFetch = false; });
  }

  function sequentialOps(items, fn) {
    return items.reduce(function (chain, item) {
      return chain.then(function () { return fn(item); });
    }, Promise.resolve());
  }

  // ─── Rate limiting ───

  function canPerformOp() {
    if (state.opsThisMinute >= MAX_OPS_PER_MINUTE) {
      console.warn('[GWP] Rate limit reached (' + MAX_OPS_PER_MINUTE + ' ops/min). Skipping.');
      return false;
    }
    state.opsThisMinute++;
    if (!state.opsResetTimer) {
      state.opsResetTimer = setTimeout(function () {
        state.opsThisMinute = 0;
        state.opsResetTimer = null;
      }, 60000);
    }
    return true;
  }

  // ─── Rules (from Liquid JSON) ───

  // Rules with a start/end time are filtered against the shopper's clock on every
  // evaluation (pages can be cached, so this can't be done in Liquid). A rule that
  // ends drops out of the list, and its gift is removed as an orphan.
  function isLive(rule) {
    var now = Date.now();
    if (rule.startsAt && now < rule.startsAt) return false;
    if (rule.endsAt && now >= rule.endsAt) return false;
    return true;
  }

  function loadRules() {
    return loadAllRules().filter(isLive);
  }

  function loadAllRules() {
    if (state.rules) return state.rules;
    var el = document.getElementById('gwp-rules');
    var rules = [];
    if (el) {
      try { rules = JSON.parse(el.textContent || '[]') || []; } catch (e) {
        console.error('[GWP] Could not parse rules JSON', e);
      }
    }
    rules = rules.filter(function (r) { return r && r.giftVariantId && r.giftAvailable !== false; });
    // Highest priority first (same ordering the ISC app API returns)
    rules.sort(function (a, b) { return (b.priority || 0) - (a.priority || 0); });
    state.rules = rules;
    return rules;
  }

  // ─── Cart helpers ───

  function getCart() {
    return fetchWithTimeout(ROUTES.cart, { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json();
    });
  }

  function addToCart(variantId, ruleId) {
    var props = { _gwp: 'true' };
    props[GWP_PROPERTY] = ruleId;
    return fetchWithTimeout(ROUTES.add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1, properties: props }] })
    }).then(function (res) { return res.json(); });
  }

  function removeFromCart(lineKey) {
    return fetchWithTimeout(ROUTES.change, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (res) { return res.json(); });
  }

  // ─── Gift detection ───

  function isGiftLine(item) {
    return !!(item.properties && item.properties[GWP_PROPERTY]);
  }

  function getCurrentGifts(cart) {
    var gifts = {};
    cart.items.forEach(function (item) {
      if (isGiftLine(item)) {
        gifts[item.properties[GWP_PROPERTY]] = { key: item.key, variantId: item.variant_id, productId: item.product_id };
      }
    });
    return gifts;
  }

  function getCartTotalExcludingGifts(cart) {
    var total = 0;
    cart.items.forEach(function (item) { if (!isGiftLine(item)) total += item.final_line_price; });
    return total / 100;
  }

  function getCartQuantityExcludingGifts(cart) {
    var qty = 0;
    cart.items.forEach(function (item) { if (!isGiftLine(item)) qty += item.quantity; });
    return qty;
  }

  function cartHasProduct(cart, productId) {
    return cart.items.some(function (item) {
      return !isGiftLine(item) && String(item.product_id) === String(productId);
    });
  }

  function cartHasCollectionProduct(cart, ids) {
    if (!ids || !ids.length) return false;
    return cart.items.some(function (item) {
      return !isGiftLine(item) && ids.indexOf(item.product_id) !== -1;
    });
  }

  // ─── Rule evaluation ───

  function evaluateRule(rule, cart) {
    switch (rule.conditionType) {
      case 'CART_TOTAL':
        return getCartTotalExcludingGifts(cart) >= Number(rule.thresholdAmount || 0);
      case 'SPECIFIC_PRODUCT':
        return cartHasProduct(cart, rule.conditionProductId);
      case 'SPECIFIC_COLLECTION':
        return cartHasCollectionProduct(cart, rule.conditionCollectionProductIds);
      case 'QUANTITY':
        return getCartQuantityExcludingGifts(cart) >= Number(rule.thresholdQuantity || 0);
      default:
        return false;
    }
  }

  function getRemainingAmount(rule, cart) {
    if (rule.conditionType !== 'CART_TOTAL') return 0;
    return Math.max(0, Number(rule.thresholdAmount || 0) - getCartTotalExcludingGifts(cart));
  }

  // Which rules actually award a gift, honouring priority + stacking.
  function resolveActiveRules(rules, cart) {
    var active = {};
    var nonStackableQualified = false;
    rules.forEach(function (rule) {
      if (!evaluateRule(rule, cart)) return;
      if (nonStackableQualified && !rule.stackable) return;
      active[rule.id] = true;
      if (!rule.stackable) nonStackableQualified = true;
    });
    return active;
  }

  // ─── Message rendering ───

  function renderMessages(rules, cart) {
    var containers = document.querySelectorAll('[data-gwp-messages]');
    if (!containers.length) return;
    var active = resolveActiveRules(rules, cart);
    var hasPaidItems = cart.items.some(function (i) { return !isGiftLine(i); });
    var html = '';

    if (hasPaidItems) {
      rules.forEach(function (rule) {
        var giftName = escapeHtml(rule.giftTitle || 'gift');
        if (active[rule.id] && rule.qualifiedMessage) {
          html += '<div class="gwp-message gwp-message--qualified">' +
            escapeHtml(rule.qualifiedMessage).replace(/\{gift\}/g, giftName) + '</div>';
        } else if (!active[rule.id] && rule.progressMessage && rule.conditionType === 'CART_TOTAL' && !evaluateRule(rule, cart)) {
          var remaining = getRemainingAmount(rule, cart).toFixed(2);
          html += '<div class="gwp-message gwp-message--progress">' +
            escapeHtml(rule.progressMessage).replace(/\{remaining\}/g, remaining).replace(/\{gift\}/g, giftName) + '</div>';
        }
      });
    }

    containers.forEach(function (c) {
      c.innerHTML = html;
      c.hidden = html === '';
    });
  }

  // ─── UI refresh (FS cart drawer + Dawn pub/sub) ───

  function refreshUI(cart) {
    if (window.FsCart && typeof window.FsCart.refresh === 'function') {
      window.FsCart.refresh();
    }
    if (typeof window.publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try { window.publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp-engine', cartData: cart || null }); } catch (e) {}
    }
    var cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') cartItems.onCartUpdate();
  }

  // ─── Core engine ───

  function processCart(cart) {
    if (state.processing) return;
    var rules = loadRules();
    if (!loadAllRules().length && !cart.items.some(isGiftLine)) return;
    if (!canPerformOp()) return;
    state.processing = true;

    var currentGifts = getCurrentGifts(cart);
    var active = resolveActiveRules(rules, cart);
    var hasPaidItems = cart.items.some(function (i) { return !isGiftLine(i); });
    var toAdd = [];
    var toRemove = [];

    rules.forEach(function (rule) {
      var hasGift = currentGifts[rule.id];
      if (active[rule.id] && hasPaidItems) {
        if (!hasGift) toAdd.push(rule);
      } else if (hasGift) {
        toRemove.push(hasGift);
      }
    });

    // Gifts from rules that were deleted/disabled since they were added
    var ruleIds = rules.map(function (r) { return r.id; });
    Object.keys(currentGifts).forEach(function (id) {
      if (ruleIds.indexOf(id) === -1) toRemove.push(currentGifts[id]);
    });

    // Gift products are only available through the GWP: any gift variant that is in
    // the cart without our rule property (added via a direct link / variant ID) is removed.
    // Checks every configured rule, including ones not yet started or already ended.
    var giftVariantIds = loadAllRules().map(function (r) { return String(r.giftVariantId); });
    cart.items.forEach(function (item) {
      if (!isGiftLine(item) && giftVariantIds.indexOf(String(item.variant_id)) !== -1) {
        toRemove.push({ key: item.key });
      }
    });

    renderMessages(rules, cart);

    if (!toAdd.length && !toRemove.length) {
      state.processing = false;
      return;
    }

    sequentialOps(toRemove, function (gift) { return removeFromCart(gift.key); })
      .then(function () {
        return sequentialOps(toAdd, function (rule) { return addToCart(rule.giftVariantId, rule.id); });
      })
      .then(getCart)
      .then(function (updatedCart) {
        lastCartToken = cartToken(updatedCart);
        renderMessages(rules, updatedCart);
        refreshUI(updatedCart);
      })
      .catch(function (err) { console.error('[GWP] Cart operation failed:', err); })
      .finally(function () {
        setTimeout(function () { state.processing = false; }, COOLDOWN_MS);
      });
  }

  // ─── Fetch interception ───
  // Every cart mutation (drawer, product form, cart page) goes through fetch,
  // so reacting to these is enough — no polling.

  var nativeFetch = window.fetch.bind(window);
  var lastCartToken = null;

  function cartToken(cart) {
    return cart.token + ':' + cart.item_count + ':' + cart.total_price;
  }

  window.fetch = function () {
    var url = arguments[0];
    var urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');
    var isCartMutation =
      urlStr.indexOf('/cart/add') !== -1 ||
      urlStr.indexOf('/cart/change') !== -1 ||
      urlStr.indexOf('/cart/update') !== -1 ||
      urlStr.indexOf('/cart/clear') !== -1;

    if (state.gwpFetch || !isCartMutation) return nativeFetch.apply(window, arguments);

    return nativeFetch.apply(window, arguments).then(function (response) {
      if (response && response.ok) {
        setTimeout(function () {
          getCart().then(function (fresh) {
            var token = cartToken(fresh);
            if (token !== lastCartToken) {
              lastCartToken = token;
              processCart(fresh);
            } else {
              renderMessages(loadRules(), fresh);
            }
          }).catch(function () {});
        }, 100);
      }
      return response;
    });
  };

  // Drawer re-opened / refreshed: keep messages current
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('.js-fs-cart-open')) return;
    getCart().then(function (c) { renderMessages(loadRules(), c); }).catch(function () {});
  });

  // ─── Initialise ───

  function init() {
    getCart().then(function (cart) {
      lastCartToken = cartToken(cart);
      processCart(cart);
      renderMessages(loadRules(), cart);
    }).catch(function () {});
  }

  window.GWPEngine = {
    rules: function () { return loadRules(); },
    allRules: function () { return loadAllRules(); },
    run: function () { return getCart().then(processCart); }
  };

  if (document.readyState === 'complete') init();
  else window.addEventListener('load', init);
})();
